import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '../contexts/ToastContext';
import styled from 'styled-components';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import { apiService } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 8px;
  color: #333;
  font-size: 28px;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 32px;
  font-size: 14px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 16px;
  padding-right: 48px;
  border: 2px solid ${props => props.$hasError ? '#dc3545' : '#e9ecef'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  transition: color 0.2s ease;
  padding: 4px;

  &:hover {
    color: #495057;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: #00d4aa;
  color: white;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #00b894;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 32px 0;
`;

const SuccessIcon = styled.div`
  color: #00d4aa;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
`;

const SuccessText = styled.p`
  color: #333;
  font-size: 16px;
  margin-bottom: 24px;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
  color: #666;
  font-size: 14px;
`;

const FooterLink = styled(Link)`
  color: #00d4aa;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

interface FormData {
  password: string;
  password_confirmation: string;
}

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);

  const { showError } = useToast();
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  useEffect(() => {
    // Detect user type from pathname
    setIsEmployee(window.location.pathname.includes('/employee'));

    if (!token || !email) {
      showError('Link inválido ou expirado');
      navigate('/app/login');
    }
  }, [token, email, navigate, showError]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);

    try {
      const resetData = {
        email: email!,
        token: token!,
        password: data.password,
        password_confirmation: data.password_confirmation,
      };

      // DEBUG: Log request data
      console.log('=== PASSWORD RESET DEBUG ===');
      console.log('Email:', resetData.email);
      console.log('Token:', resetData.token);
      console.log('Token length:', resetData.token?.length);
      console.log('Password length:', resetData.password?.length);
      console.log('Full data:', resetData);

      const response = isEmployee
        ? await apiService.resetPasswordEmployee(resetData)
        : await apiService.resetPassword(resetData);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/app/login');
        }, 3000);
      }
    } catch (err: any) {
      console.error('=== PASSWORD RESET ERROR ===');
      console.error('Error:', err);
      console.error('Response:', err.response);
      console.error('Response data:', err.response?.data);
      const errorMessage = err.response?.data?.message || 'Erro ao redefinir senha';
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Container>
        <Card>
          <SuccessContainer>
            <SuccessIcon>
              <CheckCircle size={64} />
            </SuccessIcon>
            <Title>Senha Redefinida!</Title>
            <SuccessText>
              Sua senha foi alterada com sucesso. Você será redirecionado para a página de login...
            </SuccessText>
            <FooterLink to="/app/login">Ir para login agora</FooterLink>
          </SuccessContainer>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Nova Senha</Title>
        <Subtitle>Digite sua nova senha abaixo</Subtitle>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Nova senha"
              $hasError={!!errors.password}
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: {
                  value: 8,
                  message: 'Senha deve ter pelo menos 8 caracteres'
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Senha deve conter letras maiúsculas, minúsculas e números'
                }
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmar nova senha"
              $hasError={!!errors.password_confirmation}
              {...register('password_confirmation', {
                required: 'Confirmação de senha é obrigatória',
                validate: value => value === watch('password') || 'As senhas não coincidem'
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
            {errors.password_confirmation && (
              <ErrorMessage>{errors.password_confirmation.message}</ErrorMessage>
            )}
          </InputGroup>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Redefinindo...' : 'Redefinir Senha'}
          </Button>
        </Form>

        <Footer>
          Lembrou sua senha?{' '}
          <FooterLink to="/app/login">Fazer login</FooterLink>
        </Footer>
      </Card>
    </Container>
  );
};
